// filepath: apps/web/src/admin/tenants/DeprovisionPage.tsx
//
// FR-TEN-6 [M] / FR-UI-ADM-15 [M] / ISS-050: page that combines the
// tenant list, the irreversibility dialog, and the progress panel.
// Rendered at /admin/tenants/:tenantId/deprovision (registered in
// App.tsx).
//
// Flow:
//   1. User opens /admin/tenants/:tenantId/deprovision.
//   2. <TenantList /> shows all tenants (operator view).
//   3. Click "De-provision" → <DeprovisionDialog /> mounts.
//   4. Type the slug + check the "permanent" box → "Confirm" enabled.
//   5. On confirm: useStartDeprovision POSTs the start.
//   6. <DeprovisionProgress /> polls saga status until terminal.

import React from "react";
import { TenantAdminGuard } from "./TenantAdminGuard";
import { TenantList, type TenantListItem } from "./TenantList";
import { DeprovisionDialog } from "./DeprovisionDialog";
import { DeprovisionProgress } from "./DeprovisionProgress";
import { useStartDeprovision } from "./queries";

export interface DeprovisionPageProps {
  /** Path parameter :tenantId. When absent, the list view is rendered. */
  tenantId?: string;
  /** Export path (informational only — the saga reads env at startup). */
  exportPath?: string;
  /** Tenant list for the list view. Injected so tests can pass fixtures. */
  tenants?: TenantListItem[];
  /** Override the list fetch. */
  onListTenants?: () => Promise<TenantListItem[]>;
}

export function DeprovisionPage({
  tenantId,
  exportPath,
  tenants: tenantsProp,
  onListTenants,
}: DeprovisionPageProps): React.ReactElement {
  return (
    <TenantAdminGuard>
      <main
        aria-labelledby="deprovision-title"
        data-testid="deprovision-page-root"
      >
        <h1 id="deprovision-title" className="visually-hidden">
          De-provisioning
        </h1>
        {tenantId ? (
          <TenantDeprovisionView
            tenantId={tenantId}
            exportPath={exportPath}
            tenants={tenantsProp || []}
            onListTenants={onListTenants}
          />
        ) : (
          <p data-testid="deprovision-page-no-tenant">
            Select a tenant from the list to begin a de-provisioning flow.
          </p>
        )}
      </main>
    </TenantAdminGuard>
  );
}

interface TenantDeprovisionViewProps {
  tenantId: string;
  exportPath?: string;
  tenants: TenantListItem[];
  onListTenants?: () => Promise<TenantListItem[]>;
}

function TenantDeprovisionView({
  tenantId,
  exportPath,
  tenants,
  onListTenants,
}: TenantDeprovisionViewProps): React.ReactElement {
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [tenantsState, setTenantsState] =
    React.useState<TenantListItem[]>(tenants);
  const [listError, setListError] = React.useState<string | undefined>(
    undefined,
  );
  const [listLoading, setListLoading] = React.useState(false);
  const startMut = useStartDeprovision(tenantId);

  const selected = tenantsState.find((t) => t.id === tenantId);

  const refresh = React.useCallback(async (): Promise<void> => {
    if (!onListTenants) return;
    setListLoading(true);
    setListError(undefined);
    try {
      const next = await onListTenants();
      setTenantsState(next);
    } catch (e) {
      setListError(String(e));
    } finally {
      setListLoading(false);
    }
  }, [onListTenants]);

  React.useEffect(() => {
    if (tenants.length === 0 && onListTenants) {
      void refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!selected) {
    return (
      <div data-testid="deprovision-page-unknown-tenant">
        <p>Unknown tenant: {tenantId}.</p>
        <TenantList
          tenants={tenantsState}
          isLoading={listLoading}
          error={listError}
          onDeprovision={() => undefined}
          onRefresh={() => void refresh()}
        />
      </div>
    );
  }

  return (
    <div data-testid="deprovision-page" className="deprovision-page">
      <TenantList
        tenants={tenantsState}
        isLoading={listLoading}
        error={listError}
        onDeprovision={() => setDialogOpen(true)}
        onRefresh={() => void refresh()}
      />

      <div className="deprovision-actions-row">
        <button
          type="button"
          onClick={() => setDialogOpen(true)}
          data-testid="open-deprovision-dialog"
        >
          De-provision {selected.slug}
        </button>
      </div>

      {dialogOpen ? (
        <DeprovisionDialog
          tenantSlug={selected.slug}
          tenantName={selected.name}
          exportPath={exportPath}
          isStarting={startMut.isPending}
          onCancel={() => setDialogOpen(false)}
          onConfirm={(idempotencyKey) => {
            startMut.mutate(
              { idempotencyKey, exportPath },
              {
                onSuccess: () => setDialogOpen(false),
                onError: (err) => {
                  // Keep the dialog open so the operator can retry; surface
                  // the error in a console log for now (no toast in v1).
                  // eslint-disable-next-line no-console
                  console.error("start deprovision failed", err);
                },
              },
            );
          }}
        />
      ) : null}

      <DeprovisionProgress tenantId={tenantId} />
    </div>
  );
}
